import {
    BrandConfig,
    defaultBrandConfig,
    tenantOverrideKey,
    KnownTenants,
    TenantBasicSettings,
} from '../Store/Tenant';
import { fetchWrapper } from './FetchWrapper';

import { LogException, LogMessage, SeverityLevel } from './Logging';
import { assetBlobContainer } from './Constants';

const validDomains = ['.strongbox.link', '.strongbox.ai'];

// The only entries that should be present in subdomainMap are those where the host name 
// differs from the name used to identify the tenant in blob storage.  There are legacy 
// reasons where this happens or where the hostname doesn't play well, e.g. those that 
// start with a number. Generally, it's not necessary.

const subdomainMap: Record<string, string> = {
    'dev': 'strongbox',
    'featurestest': 'strongbox',
    'armaninollp': 'armanino',
    'btcpa': 'bennettthrasher',
    'cbiz': 'cbizmhm',
    'cds-dd': 'cdsdd',
    'eidebailly-valuation': 'eidebailly-val',
    'eytest': 'ernstyoung',
    'ey-au': 'ernstyoungaustralia',
    'ey-oceania': 'ernstyoungaustralia',
    'eyca': 'ernstyoungcanada',
    '512financial': 'fivetwelvefinancial',
    'greerwalkeraudit': 'greerwalker-tasaudit',
    'greerwalker': 'greerwalker-tasaudit',
    'highrockpartners': 'highrock',
    'hl': 'houlihanlokey',
    'intrinsicfirm': 'intrinsic',
    'pennsaxton-audit': 'pennsa-audit',
    'rklcpa': 'rkl',
    'stout-fdd': 'stout-ta',
    '3pg-tas': 'threepg-tas',
    'rsmus': 'rsm',
    'sectest': 'strongbox',
    'tannerco': 'tanner',
    'vmghealth': 'vmg',
    'williamsmarston': 'wmarston',
    'reaadvisory': 'reaadvisory-tas',
    'cbiztas-se': 'marcum',
    'uhy': 'uhy-tas',
}

export const defaultBasicTenantSettings: TenantBasicSettings = {
    defaultResource: KnownTenants.defaultLender,
    showStrongboxLogo: false,
};

/*
 * If you add a TenantResourceList object make sure that you add it to tenantResourceLists
 * below.  If you don't it won't actually be available for use.
 */

export const defaultTenantName = 'strongbox';

async function TryGetBasicTenantSettings(tenantName: string) : Promise<TenantBasicSettings | undefined> {
    const url = `${assetBlobContainer}/brandassets/${tenantName}/config.json`;

    try {
        const response = await fetchWrapper(url, {}, true, false);
        if (!response.ok) {
            console.warn(`Unable to find config.json for ${tenantName}. Could be okay but might be a misconfigured blob storage name`);
            return defaultBasicTenantSettings;
        }

        return await response.json();
    } catch (exception) {
        console.warn(`exception retrieving config.json for ${tenantName} that probably indicates invalid contents. Check the values in config.json and make sure it's valid JSON`);
        console.warn(exception);
        return undefined;
    }
}

export async function GetTenantBannerPath(tenantName: string): Promise<string> {
    try {
        const url = `${assetBlobContainer}/brandassets/${tenantName}/Banner.svg`;

        const response = await fetchWrapper(url, {}, true, false);
        if (!response.ok) {
            console.warn(`Unable to find Banner SVG for ${tenantName}. Probably okay but might be a misconfigured blob storage name`);
            return `${assetBlobContainer}/brandassets/${defaultTenantName}/Banner.svg`;
        }

        return `${assetBlobContainer}/brandassets/${tenantName}/Banner.svg`;
    } catch (exception) {
        LogException(
            `Exception thrown attempting to get Banner SVG for ${tenantName}`,
            exception,
            {
                tenantName,
            }
        )
        // Probably not an error.  It's okay if there are no branding settings, just means they weren't overridden.
        return `${assetBlobContainer}/brandassets/${defaultTenantName}/Banner.svg`
    }
}

export async function GetTenantFaviconPath(tenantName: string): Promise<string> {
    try {
        const url = `${assetBlobContainer}/brandassets/${tenantName}/favicon-32x32.png`;

        const response = await fetchWrapper(url, {}, true, false);
        if (!response.ok) {
            console.warn(`Unable to find Favicon for ${tenantName}. Probably okay but might be a misconfigured blob storage name`);
            return `${assetBlobContainer}/brandassets/${defaultTenantName}/favicon-32x32.png`;
        }

        return `${assetBlobContainer}/brandassets/${tenantName}/favicon-32x32.png`;
    } catch (exception) {
        LogException(
            `Exception thrown attempting to get favicon for ${tenantName}`,
            exception,
            {
                tenantName,
            }
        )
        // Probably not an error.  It's okay if there are no branding settings, just means they weren't overridden.
        return `${assetBlobContainer}/brandassets/${defaultTenantName}/favicon-32x32.png`
    }
}

export async function GetTenantBasicSettings(tenantName: string): Promise<TenantBasicSettings> {
    if (!tenantName) {
        LogMessage(
            `tenantName is undefined in GetTenantBasicSettings, defaulting to strongbox`,
            SeverityLevel.Warning
        );
        console.error('tenantName is undefined in GetTenantBasicSettings, defaulting to strongbox');
        tenantName = KnownTenants.strongbox;
    }

    const result = await TryGetBasicTenantSettings(tenantName);
    return result || defaultBasicTenantSettings;
}

export async function GetTenantCSSConfig(tenantName: string): Promise<BrandConfig> {
    if (!tenantName) {
        LogMessage(
            `tenantName is undefined in GetTenantCSSConfig, continuing with default settings`,
            SeverityLevel.Warning
        );
        console.error('tenantName is undefined in GetTenantCSSConfig, continuing with default settings');
        tenantName = KnownTenants.strongbox;
    }

    const url = `${assetBlobContainer}/brandassets/${tenantName}/brandconfig.json`;

    try {
        const response = await fetchWrapper(url, {}, true, false);

        if (!response.ok) {
            LogMessage(
                `failed to download brand settings for ${tenantName}. Using default.`,
                SeverityLevel.Warning,
                {
                    tenantName,
                }
            );
            return defaultBrandConfig;
        }

        const config = await response.json() as BrandConfig;

        if (!!config.cssSettings) {
            const r = document.querySelector(':root') as HTMLElement;

            Object.keys(config.cssSettings).forEach(key => {
                try {
                    r.style.setProperty(key, config.cssSettings[key]);
                } catch (setPropException) {
                    // Just continue, default values get used if we don't change anything.
                }
            });
        }

        return config;

    } catch (exception) {
        LogException(
            `Exception thrown downloading brand config for ${tenantName}`,
            exception,
            {
                tenantName,
            }
        )
        // Probably not an error.  It's okay if there are no branding settings, just means they weren't overridden.
        return defaultBrandConfig;
    }
}

export async function GetTenantNameFromHostName(hostname: string): Promise<string> {
    try {
        const tenantOverride = localStorage.getItem(tenantOverrideKey);
        if (tenantOverride !== null) {
            return tenantOverride;
        }

        const hostCopy = hostname.toLowerCase();

        // .split will always produce a result with at least 1 element
        const tenantNameParts = hostCopy.split('.');
        if (tenantNameParts.length < 3) {
            return KnownTenants.strongbox;
        }
        const testDomain = `.${tenantNameParts[1]}.${tenantNameParts[2]}`;
        if (!validDomains.find(domain => domain === testDomain)) {
            return KnownTenants.strongbox;
        }

        const result = subdomainMap[tenantNameParts[0]];
        if (!!result) {
            return result;
        }

        let proposedTenant = tenantNameParts[0];

        // Try and get config.json for the tenant name.  if we get it, great, use it.  Else 
        // fall back to strongbox.

        const basicSettings = await TryGetBasicTenantSettings(proposedTenant);

        if (!basicSettings) {
            proposedTenant = 'strongbox';
        }

        return proposedTenant;
    }
    catch (exception) {
        console.error('failure trying to validate tenant name from URL');
        console.error(exception);
        
        return 'strongbox'
    }
}
